/**
 * IPTV Service - Parse and manage M3U/M3U8 playlists
 * Loads all lists from Firestore and merges channels
 */
import { getIPTVLists } from "./iptvLists";
import { isElectron } from "../utils/electron";

// CORS Proxy for fetching playlists (not needed in Electron)
const CORS_PROXY_URL = import.meta.env.VITE_CORS_PROXY_URL;

// Cache for parsed channels
let cachedChannels = null;
let cachedGroups = null;
let cachedLists = null;

/**
 * Parse M3U/M3U8 content into channel objects
 */
function parseM3U(content, listName) {
  const lines = content.split("\n");
  const channels = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith("#EXTINF:")) {
      const channel = {
        id: 0, // Will be set later
        name: "",
        logo: "",
        group: "Sin categoría",
        url: "",
        source: listName || "Desconocido",
      };

      // Extract tvg-name
      const nameMatch = line.match(/tvg-name="([^"]*)"/);
      if (nameMatch) {
        channel.name = nameMatch[1];
      }

      // Extract tvg-logo
      const logoMatch = line.match(/tvg-logo="([^"]*)"/);
      if (logoMatch) {
        channel.logo = logoMatch[1];
      }

      // Extract group-title
      const groupMatch = line.match(/group-title="([^"]*)"/);
      if (groupMatch) {
        channel.group = groupMatch[1];
      }

      // Fallback: Get name from end of line (after last comma)
      if (!channel.name) {
        const commaIndex = line.lastIndexOf(",");
        if (commaIndex !== -1) {
          let name = line.substring(commaIndex + 1).trim();
          const pipeIndex = name.lastIndexOf("|");
          if (pipeIndex !== -1) {
            const country = name.substring(pipeIndex + 1).trim();
            name = name.substring(0, pipeIndex).trim();
            if (channel.group === "Sin categoría" && country) {
              channel.group = country;
            }
          }
          channel.name = name.replace(/✪/g, "").trim();
        }
      }

      // Get URL from next non-empty, non-comment line
      for (let j = i + 1; j < lines.length && j < i + 3; j++) {
        const nextLine = lines[j]?.trim();
        if (nextLine && !nextLine.startsWith("#")) {
          channel.url = nextLine;
          i = j;
          break;
        }
      }

      if (channel.name && channel.url) {
        channels.push(channel);
      }
    }
  }

  return channels;
}

/**
 * Fetch a single playlist via CORS proxy
 */
async function fetchSinglePlaylist(list) {
  try {
    // Try direct fetch without proxy (tokens will be valid for user's IP)
    const fetchUrl = list.url;

    const response = await fetch(fetchUrl);
    if (!response.ok) {
      console.warn(`Failed to fetch ${list.name}: ${response.status}`);
      return [];
    }

    const content = await response.text();
    return parseM3U(content, list.name);
  } catch (error) {
    console.warn(`Error fetching ${list.name}:`, error);
    return [];
  }
}

/**
 * Fetch and merge all playlists from Firestore
 */
export async function fetchPlaylist() {
  try {
    // Return cached data if available
    if (cachedChannels) {
      return cachedChannels;
    }

    // Get list URLs from Firestore
    const lists = await getIPTVLists();
    cachedLists = lists;

    console.log(`Loading ${lists.length} IPTV lists...`);

    // Fetch all playlists in parallel
    const results = await Promise.all(lists.map((list) => fetchSinglePlaylist(list)));

    // Merge all channels
    let allChannels = [];
    results.forEach((channels, index) => {
      console.log(`${lists[index].name}: ${channels.length} channels`);
      allChannels = allChannels.concat(channels);
    });

    // Assign unique IDs
    allChannels.forEach((channel, index) => {
      channel.id = index + 1;
    });

    cachedChannels = allChannels;

    // Extract and cache unique groups
    const groupSet = new Set(allChannels.map((ch) => ch.group));
    cachedGroups = Array.from(groupSet).sort();

    console.log(`Total: ${allChannels.length} channels, ${cachedGroups.length} groups`);

    return cachedChannels;
  } catch (error) {
    console.error("Error fetching IPTV playlists:", error);
    return [];
  }
}

/**
 * Get all unique groups/categories
 */
export function getGroups() {
  return cachedGroups || [];
}

/**
 * Get loaded lists info
 */
export function getLoadedLists() {
  return cachedLists || [];
}

/**
 * Search channels by name
 */
export function searchChannels(query, group = null) {
  if (!cachedChannels) return [];

  let results = cachedChannels;

  if (group && group !== "all") {
    results = results.filter((ch) => ch.group === group);
  }

  if (query) {
    const lowerQuery = query.toLowerCase();
    results = results.filter((ch) => ch.name.toLowerCase().includes(lowerQuery));
  }

  return results;
}

/**
 * Clear cached data (force refresh)
 */
export function clearCache() {
  cachedChannels = null;
  cachedGroups = null;
  cachedLists = null;
}
